import {FloatingUIElementProps} from '@app/helpers/react';
import Dropdown from '@app/lui2/Dropdown';
import FaIcon from '@app/lui3/FaIcon';
import {
  cloneElement,
  ComponentProps,
  forwardRef,
  isValidElement,
  ReactElement,
  ReactNode,
  useCallback,
  useState,
} from 'react';

type ElementWithAfterIcon = ReactElement<{afterIcon?: ReactNode}>;

interface Props
  extends Omit<ComponentProps<typeof Dropdown>, 'anchor' | 'onOpenChange'> {
  anchor:
    | ElementWithAfterIcon
    | ((props: FloatingUIElementProps) => ElementWithAfterIcon);
  onOpenChange?: (open: boolean) => void;
}

const DropdownWithIndicator = forwardRef<HTMLElement, Props>(
  ({anchor, onOpenChange, ...dropdownProps}, ref) => {
    const [isOpen, setIsOpen] = useState(false);

    const handleOpenChange = useCallback(
      (open: boolean) => {
        setIsOpen(open);
        onOpenChange?.(open);
      },
      [onOpenChange]
    );

    const chevronIcon = (
      <FaIcon
        faStyle="solid"
        icon={isOpen ? 'chevron-up' : 'chevron-down'}
        size="1x"
      />
    );

    const anchorWithIcon =
      typeof anchor === 'function'
        ? (props: FloatingUIElementProps) => {
            const anchorElement = anchor(props);
            if (isValidElement(anchorElement)) {
              return cloneElement(anchorElement, {
                afterIcon: chevronIcon,
              } as any);
            }
            return anchorElement;
          }
        : isValidElement(anchor)
          ? cloneElement(anchor, {afterIcon: chevronIcon} as any)
          : anchor;

    return (
      <Dropdown
        ref={ref}
        anchor={anchorWithIcon}
        onOpenChange={handleOpenChange}
        {...dropdownProps}
      />
    );
  }
);

export default DropdownWithIndicator;
